// src/ReservationWidget.jsx
import React, { useState, useEffect } from 'react';
import { ConfigProvider, Skeleton, Typography, Button, Spin } from 'antd';
import { MemoryRouter } from "react-router-dom";
import axios from 'axios';
import WidgetForm from './WidgetForm';
import ConfirmationScreen from './ConfirmationScreen';
import Dashboard from './Dashboard';
import Menu from './Menu';
import { ORG_HOURS, TABLES } from '../mockData';
import moment from 'moment';
import Offers from "./Offers";
import RateYourDine from "./RateYourDine";
import AuthFlow from './AuthFlow';
import configFile from "./config";

const processReservations = (resList) =>
  resList.map((res) => ({
    id: res.id,
    customerName: res.guest_name,
    phone: res.contact_number,
    email: res.email,
    partySize: res.party_size,
    start: new Date(
      `${moment(res.reservation_date).format('YYYY-MM-DD')}T${res.reservation_time}.000Z`
    ).toISOString(),
    end: new Date(
      new Date(
        `${moment(res.reservation_date).format('YYYY-MM-DD')}T${res.reservation_time}.000Z`
      ).getTime() +
        res.expected_duration_minutes * 60000
    ).toISOString(),
    status: res.status.charAt(0).toUpperCase() + res.status.slice(1),
    notes: res.special_requests || '',
    occasion: '',
    tableId: res.assigned_table_id,
    source: res.reservation_source,
  }));

const SESSION_EXPIRATION_HOURS = 24;
const { Title, Paragraph } = Typography;
const apiURL = configFile.API_URL;

const defaultConfig = {
  colors: {
    primary: '#3B82F6',
    text: '#1f2937',
    background: '#f3f4f6',
    card: '#ffffff',
    muted: '#6b7280',
    buttonText: '#ffffff',
    buttonHover: '#2563eb',
    reservationButton: '#3B82F6',
  },
  font: '"Playfair Display", serif',
  requiredFields: ['name', 'phone', 'guests'],
  showTimePicker: true,
  minGuests: 1,
  maxGuests: 10,
  allowSpecialRequests: true,
  autoConfirmation: true,
  minAdvance: 2,
  maxAdvance: 30,
  showLogo: false,
  headerText: 'Reserve Your Table',
  headerFontSize: '1.5rem',
  showCompanyInfo: false,
  contactInfoContent: '',
  confirmationMessage: 'Your reservation has been confirmed!',
  reservationButtonText: 'Reserve Now',
  digitalMenuText: 'Menu',
  is_active: true,
  company_logo: '',
  company_name: '',
  company_phone: '',
  company_address: '',
};

/** Generic popup wrapper */
const PopupWrapper = ({
  title,
  onBack,
  onClose,
  children,
  sessionActive,
  currentPage,
  onDashboard,
  onLogout,
  onMenu,
  onLogin,
  onOffers,
  config,
}) => {
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, []);

  return (
    <div className="popup-overlay">
      <div className="popup-container animate-in" style={{ fontFamily: config.font }}>
        <div className="popup-header">
          <div className="popup-left">
            {onBack && currentPage !== "auth-blocking" && (
              <button className="popup-back" onClick={onBack}>
                Back
              </button>
            )}
            {!onDashboard && !onMenu && !onLogin && !onOffers && currentPage !== "auth" && currentPage !== "auth-blocking" && (
              <span className="popup-title">{title}</span>
            )}
          </div>
          <div className="popup-actions">
            <button
              className="popup-link"
              onClick={() => {
                if (sessionActive) {
                  onDashboard();
                } else {
                  onLogin();
                }
              }}
            >
              Reservations
            </button>
            <button className="popup-link" onClick={onMenu}>
              {config.digitalMenuText || "Menu"}
            </button>
            <button className="popup-link" onClick={onOffers}>
              Offers
            </button>
            {!sessionActive ? (
              <button className="popup-link signin" onClick={onLogin}>
                Sign In
              </button>
            ) : (
              <button className="popup-link logout" onClick={onLogout}>
                Sign Out
              </button>
            )}
          </div>
          <button className="popup-close" onClick={onClose}>
            X
          </button>
        </div>
        <div className="popup-body">{children}</div>
      </div>

      <style>{`
        .popup-overlay {
          position: fixed;
          inset: 0;
          background: rgba(0,0,0,0.4);
          display: flex;
          justify-content: flex-end;
          align-items: stretch;
          z-index: 2000;
          overflow: hidden;
        }
        .popup-container {
          background: #fff;
          width: 80%;
          height: 100vh;
          border-left: 1px solid #e5e7eb;
          display: flex;
          flex-direction: column;
          transform: translateX(100%);
          animation: slideIn 0.35s ease forwards;
        }
        @keyframes slideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        .popup-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 12px 20px;
          border-bottom: 1px solid #eee;
          background: #f9fafb;
        }
        .popup-left {
          display: flex;
          align-items: center;
          gap: 12px;
        }
        .popup-logo {
          height: 36px;
        }
        .popup-title {
          font-size: ${config.headerFontSize || "1.25rem"};
          font-weight: 600;
          color: ${config.colors.text};
        }
        .popup-actions {
          display: flex;
          gap: 18px;
          margin-left: auto;
        }
        .popup-link {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 0.95rem;
          font-weight: 600;
          color: #374151;
          padding: 6px 10px;
          border-radius: 6px;
          transition: background 0.2s, color 0.2s;
        }
        .popup-link:hover {
          background: ${config.colors.primary}15;
          color: ${config.colors.primary};
        }
        .popup-link.signin {
          background: ${config.colors.primary};
          color: ${config.colors.buttonText || "#fff"};
          font-weight: 600;
          padding: 6px 14px;
          border-radius: 20px;
          box-shadow: 0 2px 6px rgba(0,0,0,0.1);
          transition: all 0.2s ease;
        }
        .popup-link.signin:hover {
          background: ${config.colors.buttonHover || "#1d4ed8"};
          transform: translateY(-1px);
        }
        .popup-link.logout {
          color: #dc2626;
        }
        .popup-link.logout:hover {
          background: #fee2e2;
        }
        .popup-back {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1rem;
          color: #374151;
        }
        .popup-close {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 1.2rem;
          font-weight: bold;
          color: #374151;
        }
        .popup-close:hover {
          color: #111827;
        }
        .popup-body {
          flex: 1;
          overflow-y: auto;
          padding: 16px;
        }
      `}</style>
    </div>
  );
};

const ReservationWidget = ({ orgId }) => {
  const [reservations, setReservations] = useState({ upcoming: [], past: [] });
  const [confirmation, setConfirmation] = useState(null);
  const [showConfirmationLoader, setShowConfirmationLoader] = useState(false);
  const [config, setConfig] = useState(defaultConfig);
  const [isLoading, setIsLoading] = useState(true);
  const [prefill, setPrefill] = useState(null);
  const [isDashboard, setIsDashboard] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [isSessionExpired, setIsSessionExpired] = useState(false);
  const [currentPage, setCurrentPage] = useState('main');
  const [isFetchingReservations, setIsFetchingReservations] = useState(false);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [selectedReservation, setSelectedReservation] = useState(null);
  const [offersCount, setOffersCount] = useState(0);
  const [pendingReservation, setPendingReservation] = useState(null);
  const [pageLoading, setPageLoading] = useState({});

  const sessionActive = !!localStorage.getItem('verifiedContact');

  useEffect(() => {
    if (!orgId) { setIsLoading(false); return; }
    let cancelled = false;
    const fetchConfig = async () => {
      setIsLoading(true);
      try {
        const token = localStorage.getItem('token');
        const { data } = await axios.get(`${apiURL}/pos/reservations/config`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { orgId },
        });
        if (!cancelled) {
          setConfig({
            ...defaultConfig,
            ...(data.config || {}),
            company_logo: data.company_logo || defaultConfig.company_logo,
            company_name: data.company_name || defaultConfig.company_name,
            company_phone: data.company_phone || defaultConfig.company_phone,
            company_address: data.company_address || defaultConfig.company_address,
          });
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching config:', error);
          setConfig(defaultConfig);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    fetchConfig();
    return () => { cancelled = true; };
  }, [orgId]);

  useEffect(() => {
    const fetchOffersCount = async () => {
      try {
        const { data } = await axios.get(`${apiURL}/pos/myoffers/listings`, {
          params: { code: orgId, countOnly: true },
        });
        setOffersCount(data?.count || 0);
      } catch (error) {
        console.error("Error fetching offers count:", error);
        setOffersCount(0);
      }
    };
    if (orgId) fetchOffersCount();
  }, [orgId]);

  const refetchReservations = async () => {
    const verifiedContact = JSON.parse(localStorage.getItem('verifiedContact'));
    if (!verifiedContact) return;
    setIsFetchingReservations(true);
    try {
      const token = localStorage.getItem('token');
      const params = { org_id: orgId };
      if (verifiedContact.method === 'phone') {
        params.contact_number = verifiedContact.value;
      } else if (verifiedContact.method === 'email') {
        params.email = verifiedContact.value;
      }
      const { data } = await axios.get(`${apiURL}/pos/reservations/myreservations`, {
        headers: { Authorization: `Bearer ${token}` },
        params,
      });
      setReservations({
        upcoming: processReservations(data.upcoming || []),
        past: processReservations(data.past || []),
      });
    } catch (error) {
      console.error('Error fetching reservations:', error);
      setReservations({ upcoming: [], past: [] });
    } finally {
      setIsFetchingReservations(false);
    }
  };

  useEffect(() => {
    if (isDashboard && orgId) {
      refetchReservations();
    }
  }, [isDashboard, orgId]);

  const proceedWithConfirmation = (reservation, isEdit = false) => {
    setShowConfirmationLoader(true);
    setShowForm(false);
    setPrefill(null);
    setTimeout(() => {
      setConfirmation({ ...reservation, isEdit });
      setCurrentPage("confirmation");
      setShowConfirmationLoader(false);
      refetchReservations();
    }, 600);
  };

  const handleSubmit = (reservation, isEdit = false) => {
    if (!sessionActive) {
      setPendingReservation({ ...reservation, isEdit });
      setCurrentPage("auth-blocking");
      return;
    }
    proceedWithConfirmation(reservation, isEdit);
  };

  const handleValidationSuccess = () => {
    if (pendingReservation) {
      proceedWithConfirmation(pendingReservation);
      setPendingReservation(null);
    } else {
      setIsDashboard(true);
      setShowForm(false);
      setCurrentPage("dashboard");
      refetchReservations();
    }
  };

  const handleRate = (reservation) => {
    setSelectedReservation(reservation);
    setCurrentPage("rate");
  };

  const handleReset = () => {
    setConfirmation(null);
    setIsDashboard(true);
    refetchReservations();
  };

  const handleSignOut = () => {
    localStorage.removeItem('verifiedContact');
    setIsDashboard(false);
    setShowForm(true);
    setPrefill(null);
    setConfirmation(null);
    setReservations({ upcoming: [], past: [] });
  };

  const handleAddReservation = () => {
    setShowForm(true);
    setPrefill(null);
    setConfirmation(null);
    setIsDashboard(false);
  };

  const handleReschedule = (reservation) => {
    setShowForm(true);
    setPrefill(reservation);
    setIsDashboard(false);
  };

  const handleCancelReservation = async (id) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${apiURL}/pos/reservations/${id}`,
        { status: "cancelled" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      refetchReservations();
    } catch (error) {
      console.error("Error cancelling reservation:", error);
      alert("Failed to cancel reservation. Please try again.");
    }
  };

  const handleRevalidate = () => {
    setIsSessionExpired(false);
    setShowForm(true);
  };

  const goTo = (page) => {
    setPageLoading({ ...pageLoading, [page]: true });
    setTimeout(() => {
      setCurrentPage(page);
      setShowForm(page === 'main');
      setPageLoading({ ...pageLoading, [page]: false });
    }, 150);
  };

  const goToMenu = () => goTo("menu");
  const goToOffers = () => goTo("offers");
  const goToMain = () => goTo("main");
  const goToDashboard = () => { if (sessionActive) goTo("dashboard"); else goTo("auth"); };
  const handleLogin = () => goTo("auth");
  const closePopup = () => {
    setIsPopupOpen(false);
    setConfirmation(null);
    setIsDashboard(false);
    setCurrentPage('main');
    setShowForm(false);
    setPendingReservation(null);
  };

  if (isLoading) return <Skeleton active />;

  return (
    <>
      <style>{`
        .reserve-now-tab {
          position: fixed;
          top: 50%;
          right: 0;
          transform: translateY(-50%) rotate(-90deg);
          transform-origin: right bottom;
          padding: 10px 20px;
          font-size: 16px;
          font-weight: 600;
          border: none;
          border-radius: 6px 6px 0 0;
          cursor: pointer;
          z-index: 1500;
          box-shadow: 0 2px 6px rgba(0,0,0,0.2);
          white-space: nowrap;
          transition: background-color 0.2s ease;
        }
        .vertical-tab.offers {
          background-color: #f59e0b;
          color: #fff;
        }
        .vertical-tab.offers:hover {
          background-color: #d97706;
        }
      `}</style>

      <ConfigProvider theme={{ token: { colorPrimary: config.colors.primary, fontFamily: config.font } }}>
        <div style={{ minWidth: 360, padding: 16, background: '#fff', borderRadius: 8 }}>

          {!isPopupOpen ? (
            <div style={{
              position: "fixed",
              top: "50%",
              right: 0,
              transform: "translateY(-50%)",
              zIndex: 1500,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-end",
              gap: "2px",
            }}>
              {offersCount > 0 && (
                <div style={{
                  background: `linear-gradient(135deg, ${config.colors.reservationButton || config.colors.primary} 0%, ${config.colors.buttonHover || "#2563eb"} 70%, ${config.colors.reservationButton || config.colors.primary} 100%)`,
                  color: config.colors.buttonText || "#fff",
                  fontSize: "13px",
                  fontWeight: 600,
                  padding: "4px 8px",
                  borderRadius: "10px",
                  textAlign: "center",
                  width: "fit-content",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                  marginRight: "2px",
                  display: "inline-block",
                  marginTop: "80px",
                }}>
                  {offersCount} {offersCount === 1 ? "Offer" : "Offers"}
                </div>
              )}
              <button
                className="reserve-now-tab"
                style={{
                  backgroundColor: config.colors.reservationButton || config.colors.primary,
                  color: config.colors.buttonText || "#fff",
                  padding: "16px 16px",
                  fontSize: "16px",
                  fontWeight: 600,
                  border: "none",
                  borderRadius: "6px 6px 0 0",
                  cursor: "pointer",
                  boxShadow: "0 2px 6px rgba(0,0,0,0.25)",
                  whiteSpace: "nowrap",
                  transition: "background-color 0.2s ease, transform 0.2s ease",
                  transform: "rotate(-90deg)",
                  transformOrigin: "right bottom",
                  minWidth: "120px",
                  minHeight: "60px",
                }}
                onClick={() => { setIsPopupOpen(true); goTo('main'); }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor = config.colors.buttonHover || config.colors.primary;
                  e.currentTarget.style.transform = "rotate(-90deg) scale(1.05)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor = config.colors.reservationButton || config.colors.primary;
                  e.currentTarget.style.transform = "rotate(-90deg)";
                }}
              >
                {config.reservationButtonText || "Reserve Now"}
              </button>
            </div>
          ) : isSessionExpired ? (
            <div style={{ textAlign: "center", padding: 24 }}>
              <Title level={4}>Session Expired</Title>
              <Paragraph>Please re-validate your number.</Paragraph>
              <Button type="primary" onClick={handleRevalidate}>
                Validate Number
              </Button>
            </div>
          ) : (
            <PopupWrapper
              title={currentPage === 'main' ? (prefill ? 'Update Reservation' : config.headerText) : undefined}
              onBack={currentPage !== 'main' && currentPage !== 'auth-blocking' ? goToMain : null}
              onClose={closePopup}
              sessionActive={sessionActive}
              currentPage={currentPage}
              onDashboard={goToDashboard}
              onLogout={handleSignOut}
              onMenu={goToMenu}
              onOffers={goToOffers}
              onLogin={handleLogin}
              config={config}
            >
              {pageLoading[currentPage] && (
                <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                  <Spin size="large" />
                </div>
              )}

              {currentPage === 'main' && !pageLoading.main && (
                <WidgetForm
                  config={config}
                  orgHours={ORG_HOURS}
                  tables={TABLES}
                  reservations={[...reservations.upcoming, ...reservations.past]}
                  orgId={orgId}
                  onSubmit={handleSubmit}
                  prefill={prefill}
                  onCancel={closePopup}
                  refetchReservations={refetchReservations}
                  onViewMenu={goToMenu}
                  onGoDashboard={goToDashboard}
                />
              )}

              {currentPage === 'confirmation' && !pageLoading.confirmation && (
                showConfirmationLoader ? (
                  <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                    <Spin size="large" />
                    <p style={{ marginTop: 16, color: '#666' }}>Finalizing...</p>
                  </div>
                ) : confirmation && (
                  <ConfirmationScreen config={config} reservation={confirmation} onReset={handleReset} onAddAnother={handleAddReservation} />
                )
              )}

              {currentPage === "menu" && !pageLoading.menu && (
                <MemoryRouter>
                  <Menu config={config} code="U2FsdGVkX19DooWdCIYFC%2FKNEkHuchz3RPMP8jTTDVM%3D" type="reservation" onBack={goToMain} />
                </MemoryRouter>
              )}

              {currentPage === "rate" && !pageLoading.rate && selectedReservation && (
                <RateYourDine reservation={selectedReservation} config={config} onBack={goToDashboard} />
              )}

              {currentPage === "offers" && !pageLoading.offers && (
                <Offers config={config} orgId={orgId} />
              )}

              {currentPage === 'dashboard' && !pageLoading.dashboard && (
                <Dashboard
                  config={config}
                  upcoming={reservations.upcoming}
                  past={reservations.past}
                  onAddReservation={handleAddReservation}
                  onReschedule={handleReschedule}
                  onCancel={handleCancelReservation}
                  onSignOut={handleSignOut}
                  onViewMenu={goToMenu}
                  onRate={handleRate}
                  isFetching={isFetchingReservations}
                />
              )}

              {currentPage === 'auth' && !pageLoading.auth && (
                <AuthFlow config={config} onSuccess={handleValidationSuccess} onCancel={goToMain} />
              )}

              {currentPage === 'auth-blocking' && (
                <div style={{
                  position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', zIndex: 3000, backdropFilter: 'blur(10px)'
                }}>
                  <AuthFlow
                    config={config}
                    onSuccess={() => { proceedWithConfirmation(pendingReservation); setPendingReservation(null); }}
                    onCancel={() => { setPendingReservation(null); goTo('main'); }}
                  />
                </div>
              )}
            </PopupWrapper>
          )}
        </div>
      </ConfigProvider>
    </>
  );
};

export default ReservationWidget;